import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import useForm from '@sm/hooks/useForm';

import Button from '@components/custom/Button';
import Input from '@components/custom/Input';
import ReCaptchaValidation from "@modules/login/ReCaptchaValidation";

import '@assets/css/login.css';

import appStoreIcon from '@assets/images/app-store.png';
import playMarketIcon from '@assets/images/play-market.png';
import FxmbLogo from '@assets/images/fxmb/fxmb-logo.png';
import Mids88Logo from '@assets/images/88mids/88mids-logo.png';

import { whiteLabel } from "@sm/config";
import { loginActions } from '@sm/services/loginActions';
import { showAlert } from '@sm/store/alertSlice';

const Login = () => {
  const fields = {
    username: '',
    password: '',
    code: ''
  };

  const fieldRules = [{
    name: "username",
    type: "isString",
    rules: {
      required: true
    }
  }, {
    name: "password",
    type: "isString",
    rules: {
      required: true
    }
  }, {
    name: "code",
    type: "isString",
    rules: {
      required: false
    }
  }];

  const { form, setField, validateForm } = useForm(fields, fieldRules);
  const dispatch = useDispatch();

  const [step, setStep] = useState(1);
  const [qrCode, setQrCode] = useState(null);
  const [hash, setHash] = useState('');
  const [secret, setSecret] = useState('');
  const [recaptchaResponse, setRecaptchaResponse] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  function inputChangedHandler(event, fieldName) {
    const { value } = event.target;
    let updatedField = { ...form[fieldName] };
    setField(fieldName, value);

    if (fieldName === 'code') {
      if (value.length <= 6) {
        updatedField = value;
      }
    } else {
      updatedField = value;
    }

    setField(fieldName, updatedField);

    if (fieldName === 'code' && value?.length === 6) {
      loginHandler();
    }
  };

  function loginHandler(event) {
    event?.preventDefault();
    setIsLoading(true);
    if (step === 1) {
      if (validateForm()) {
        loginActions.login(form.username, form.password, recaptchaResponse)
          .then((response) => {
            if (response && response.accessToken) {
              window.location.href = '/dashboard';
            } else {
              setStep(2);
              setQrCode(response.qrCode || null);
              setHash(response.hash || '');
              setSecret(response.secret || '');
            }
          }
          ).catch(err => {
            dispatch(showAlert('danger', err?.message));
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    } else if (step === 2) {
      if (form.code) {
        loginActions.totpLogin(secret, hash, form.code)
          .then((response) => {
            if (response && response.accessToken) {
              window.location.href = '/dashboard';
            }
          })
          .catch(err => {
            dispatch(showAlert('danger', err?.message));
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    }
      
  };

  function onRecaptchaChange(value) {
    setRecaptchaResponse(value);
  };

  return (
    <div className='login shadow'>
      <div className='loginPanel'>
        {step === 1 && (
          <p className='title text-center'>Welcome back!</p>
        )}
        <form className='loginForm shadow text-center' onSubmit={ loginHandler }>
          {step === 1 && (
            <>
              <div className='logo'>
                {whiteLabel === 'fxmb' ? (
                  <img src={ FxmbLogo } alt="FXMB" style={ { width: '160px' } } />
                ) : whiteLabel === "88mids" && (
                  <img src={ Mids88Logo } alt="88mids" style={ { width: '150px' } } />
                )}
              </div>
              <p className='text'>Sign in to your account</p>
            </>
          )}
          {step === 1 ? (
            <>
              <Input
                id='username'
                label='Username'
                type='text'
                error={ form.errors.has('username') }
                autoComplete="false"
                errorMessage={ form.errors.get('username') }
                value={ form.username || '' }
                onChange={ (event) => inputChangedHandler(event, "username") }
              />
              <Input
                id='password'
                label='Password'
                type='password'
                error={ form.errors.has('password') }
                autoComplete="false"
                className='password-input'
                errorMessage={ form.errors.get('password') }
                value={ form.password || '' }
                onChange={ (event) => inputChangedHandler(event, "password") }
              />
              <div className="recaptcha">
                <ReCaptchaValidation
                  handleChange={ (value) => onRecaptchaChange(value) }
                />
              </div>
            </>
          ) : (
            <>
              {qrCode && (
                <div className="two-fa-form text-center">
                  <p> Open Google Authenticator app on your phone </p>
                  <p> and scan the QR code below </p>
                  <img src={ qrCode } alt="qrCode" className="qr-code" />
                  <p> If you don&apos;t have Google Authenticator app installed </p>
                  <p> you can download it first for your smartphone. </p>
                    
                  <div className="buttons">
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en_us"
                      className="btn btn-secondary download-link"
                    >
                      <img src={ playMarketIcon } alt="Google Play Market" />
                    </a>
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href="https://apps.apple.com/us/app/google-authenticator/id388497605"
                      className="btn btn-secondary download-link"
                    >
                      <img src={ appStoreIcon } alt="Apple Store" />
                    </a>
                  </div>
                </div>
              )}
              <p className='text'>Enter verification code</p>
              <Input
                id='code'
                label='Verification code'
                type='text'
                error={ false }
                autoComplete="false"
                errorMessage=''
                value={ form.code || '' }
                onChange={ (event) => inputChangedHandler(event, "code") }
              />
            </>
          )}

          { step === 1 && (
            <Button
              type="submit"
              label='Sign in'
              disabled={ isLoading }
            />
          )}

        </form>
      </div>
    </div>
  );
  // }
};

export default Login;
